import React, { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ChatWidget } from "../package/dist/index.es.js";
import "./theme.css";
import { AxelLogo, AxelLogoCarOnly, AxelStar } from "./AxelLogo.jsx";
import "../package/dist/index.css";

const DEFAULT_STRICT_MODE = true;

const getOpenFromUrlParam = () => {
  if (typeof window === "undefined") {
    return true;
  }

  const openChatParam = new URLSearchParams(window.location.search).get(
    "openChat"
  );

  if (openChatParam === "false") return false;
  if (openChatParam === "true") return true;
  return false;
};

const ensureContainer = (container) => {
  if (!container) {
    throw new Error("Axel: container is required");
  }

  const target =
    typeof container === "string"
      ? document.querySelector(container)
      : container;

  if (!target) {
    throw new Error(`Axel: container '${container}' not found`);
  }

  return target;
};

const renderWidget = (container, props, strictMode) => {
  const root = createRoot(container);

  const element = React.createElement(ChatWidget, props);
  const tree = strictMode
    ? React.createElement(StrictMode, null, element)
    : element;

  root.render(tree);

  return () => root.unmount();
};


const Axel = {
  init({ container, props, strictMode = DEFAULT_STRICT_MODE }) {
    const target = ensureContainer(container);
    const open = getOpenFromUrlParam();

    const brandedProps = {
      ...props,
      options: {
        ...props.options,
        dialog: {
          ...props.options?.dialog,
          open,
        },
        
        branding: {
          name: "Axel",
          strings: {
            header: "Ask Axel",
            inputPlaceholder: "Ask away...",
            triggerTooltip: "Need help? Ask Axel!",
          },
          beta: false,
          theme: "axel",
          icons: {
            trigger: {
              text: "Ask Axel",
              mobileTrigger: {
                shape: "circle",
                className: "wgt-axel-trigger-pill",
                icon: (
                  <div className="wgt-relative wgt-flex wgt-h-full wgt-w-full wgt-items-center wgt-justify-center">
                    <AxelLogoCarOnly />
                    <AxelStar />
                  </div>
                ),
              },
              desktopTrigger: {
                shape: "pill",
                className: "wgt-axel-trigger-pill",
                icon: (
                  <div>
                    <AxelLogo circleStroke={5} />
                  </div>
                ),
              },
            },
            newChat: {
              icon: "New chat",
            },
            session: {
              icon: (
                <div className="wgt-axel-session">
                  <AxelLogo circleStroke={3} size={70} />
                </div>
              ),
            },
            bot: {
              icon: (
                <div className="wgt-axel-bot">
                  <AxelLogo circleStroke={3} />
                </div>
              ),
            },
          },
          predefinedPrompt: {
            title: "Hi Axel, what can you help me with?",
            prompt:
              "Hi Axel, can you let me know what you can do to help me and the functions you have available?",
          },
        },
        legal: {
          customTerms: [
            "Axel is provided by Auto Grab Pty Ltd (“AutoGrab”). Axel provides responses to help you with your car buying needs by using AI technology. CommBank does not provide Axel, and is not responsible or liable for Axel and any responses generated by it.",
            "Any information provided by Axel is factual in nature and does not constitute financial product advice. It does not take into account your personal circumstances, financial objectives or needs and you should seek professional advice before deciding whether a financial product may be right for you. Responses from Axel do not represent the views of CommBank or Vyro.",
          ],
          privacyPolicyUrl:
            "https://www.carsforcommbank.com.au/axel-ai-privacy-policy",
          termsUrl: "https://www.carsforcommbank.com.au/axel-ai-terms-of-use",
        },
        page: {
          size: {
            height: "100dvh",
          },
        },
      },
    };


    const unmount = renderWidget(target, brandedProps, strictMode);

    return { unmount };
  },
};

if (typeof window !== "undefined") {
  window.Axel = Axel;

  if (typeof window.AxelWrapper === "function") {
    try {
      window.AxelWrapper(Axel);
    } catch (error) {
      console.error("Error occurred while initializing AxelWrapper:", error);
    }
  }
}

export { Axel, ChatWidget };
export default Axel;
